import { useState, useEffect } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  FolderTree,
} from 'lucide-react'
import { usePlanVersionLines } from '@/hooks/usePlanVersionLines'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { cn } from '@/utils/cn'
import type { SearchablePlan } from '@/api/planSearch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface PlanPreviewProps {
  plan: SearchablePlan
  onChangeSource: () => void
  tableTitle?: string
  itemCountLabel?: string
}

const PAGE_SIZE_OPTIONS = [5, 10, 20]

export function PlanPreview({
  plan,
  onChangeSource,
  tableTitle = 'Plan Items',
  itemCountLabel = 'items in this plan',
}: PlanPreviewProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)
  const { setSourceVersionId } = usePlanWizardStore()

  const { data, isLoading, planDetails } = usePlanVersionLines(
    plan.id,
    page,
    pageSize
  )

  // Store the active_version_id when it's fetched
  useEffect(() => {
    if (planDetails?.active_version_id) {
      setSourceVersionId(planDetails.active_version_id)
    }
  }, [planDetails?.active_version_id, setSourceVersionId])

  const handlePageSizeChange = (value: string) => {
    const newPageSize = parseInt(value, 10)
    setPageSize(newPageSize)
    setPage(1) // Reset to first page when changing page size
  }

  const handlePreviousPage = () => {
    setPage(p => Math.max(1, p - 1))
  }

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      setPage(p => p + 1)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Plan Info Card */}
      <div className="bg-white p-[26px] rounded-[12px] border-2 border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]">
        <div className="flex items-center justify-between">
          {/* Left: Plan info */}
          <div className="flex flex-col gap-1">
            {/* Plan Name - 24px semibold */}
            <h3 className="text-[24px] font-semibold text-[#1C1C1C] leading-[32px]">
              {plan.name}
            </h3>
            {/* Meta info row */}
            <div className="flex items-center gap-4 text-[14px] text-[#556374]">
              {/* Hierarchy with icon */}
              <div className="flex items-center gap-1">
                <FolderTree className="h-4 w-4" />
                <span>{plan.hierarchy}</span>
              </div>
            </div>
          </div>

          {/* Right: Change source button */}
          <button
            type="button"
            onClick={onChangeSource}
            className="flex items-center gap-2 px-4 py-2 bg-[#F0F7FE] rounded-[6px] text-[14px] font-medium text-[#192A52] hover:bg-[#E0EFFD] cursor-pointer transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Change source plan
          </button>
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)] overflow-hidden">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <h4 className="text-[16px] font-semibold text-[#1C1C1C]">
            {tableTitle}
          </h4>
          <p className="text-[14px] text-[#556374] mt-1">
            {data?.totalCount ?? 0} {itemCountLabel}
          </p>
        </div>

        {/* Table Content */}
        {isLoading ? (
          <div className="px-6 py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#556374]" />
            <span className="text-[14px] text-[#556374]">Loading items...</span>
          </div>
        ) : data?.items && data.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8F8F8] border-b border-[#E5E7EB]">
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#556374] uppercase tracking-wide">
                      SKU #
                    </th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#556374] uppercase tracking-wide">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, index) => (
                    <tr
                      key={item.id}
                      className={cn(
                        'border-b border-[#E5E7EB] last:border-b-0',
                        index % 2 === 1 && 'bg-[#FAFAFA]'
                      )}
                    >
                      <td className="px-6 py-4 text-[14px] text-[#1C1C1C] font-medium">
                        {item.skuNumber}
                      </td>
                      <td className="px-6 py-4 text-[14px] text-[#556374]">
                        {item.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-[#E5E7EB] flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Rows per page */}
              <div className="flex items-center gap-2">
                <span className="text-[14px] text-[#556374]">
                  Rows per page:
                </span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <SelectItem key={size} value={size.toString()}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Page info and navigation */}
              <div className="flex items-center gap-4">
                <span className="text-[14px] text-[#556374]">
                  Page {page} of {data.totalPages}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handlePreviousPage}
                    disabled={page === 1}
                    className={cn(
                      'p-1.5 rounded-md transition-colors cursor-pointer',
                      page === 1
                        ? 'text-[#D6DAE1] cursor-not-allowed'
                        : 'text-[#556374] hover:bg-[#F8F8F8]'
                    )}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={page >= data.totalPages}
                    className={cn(
                      'p-1.5 rounded-md transition-colors cursor-pointer',
                      page >= data.totalPages
                        ? 'text-[#D6DAE1] cursor-not-allowed'
                        : 'text-[#556374] hover:bg-[#F8F8F8]'
                    )}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-6 py-12 text-center">
            <span className="text-[14px] text-[#556374]">
              No items in this plan
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
